/**
 * faceShape — classify a face into a shape category from MediaPipe FaceLandmarker
 * landmarks, and map that shape to the eyewear frame shapes that suit it.
 *
 * Pure + client-side. Uses only normalized landmark coordinates (x,y in 0..1).
 *
 * Method (based on published craniofacial-ratio classification, adapted for the
 * MediaPipe 468-pt mesh):
 *   • R1 = face length / cheekbone width is the dominant predictor. The literature
 *     puts oval at R1 ≈ 1.4–1.6, rounder/squarer below, oblong above.
 *   • MediaPipe's mesh tops out at the brow, NOT the hairline (trichion), so the
 *     raw top→chin span under-reads the true face length. We ESTIMATE the hairline
 *     by extrapolating above the brow by a fraction of the (brow→chin) lower-face
 *     height — anthropometrically the forehead is ~1/3 of total face height.
 *   • Widths are averaged over SEVERAL landmark pairs per band (forehead / cheek /
 *     jaw) so one noisy point can't skew the result.
 *   • Jaw taper (cheek width vs jaw width) separates Square (wide jaw) from the
 *     tapering shapes; forehead-vs-jaw separates Heart; cheek-widest → Diamond.
 *
 * Tunable thresholds live in THRESHOLDS so calibration is a one-spot change.
 */

import { FRAME_SHAPES } from '../../types';

export type FaceShape = 'Oval' | 'Round' | 'Square' | 'Heart' | 'Oblong' | 'Diamond';

export interface FaceShapeResult {
  shape: FaceShape;
  /** R1: estimated face length / cheekbone width (dominant predictor). */
  r1: number;
  /** jaw width / cheekbone width (≤1; smaller = more tapered jaw). */
  jaw: number;
  /** forehead width / cheekbone width. */
  forehead: number;
}

interface Pt { x: number; y: number }

// ── MediaPipe FaceLandmarker (468-pt) indices ──
const OVAL_TOP = 10;       // top of the face oval (just above the brow)
const BROW_MID = 9;        // glabella (between brows)
const NOSE_BASE = 2;       // sub-nasale (base of nose)
const CHIN = 152;          // menton (bottom of chin)
// Forehead / temple width pairs (upper face).
const FOREHEAD_PAIRS: [number, number][] = [[54, 284], [21, 251], [71, 301]];
// Jaw (gonion) width pairs (lower face).
const JAW_PAIRS: [number, number][] = [[172, 397], [136, 365], [58, 288]];

const xw = (a: Pt, b: Pt) => Math.abs(a.x - b.x);
const yh = (a: Pt, b: Pt) => Math.abs(a.y - b.y);

/** Average horizontal width across several landmark pairs (ignores missing ones). */
function avgWidth(lms: Pt[], pairs: [number, number][]): number {
  let sum = 0, n = 0;
  for (const [a, b] of pairs) {
    const pa = lms[a], pb = lms[b];
    if (pa && pb) { sum += xw(pa, pb); n++; }
  }
  return n ? sum / n : 0;
}

/** Tunable thresholds — adjust here after reading the on-screen debug ratios. */
export const THRESHOLDS = {
  // Forehead allowance: estimated hairline sits this fraction of the OVAL-TOP→chin
  // height ABOVE the oval top. The oval top is already near the brow, so only a
  // small allowance is needed to reach the trichion (hairline).
  foreheadAllowance: 0.18,
  oblongR1: 1.52,    // R1 above this → long face
  ovalMinR1: 1.35,   // oval band lower bound
  // Jaw/forehead are normalized to the FULL face width (widest span), so the
  // inner jaw/forehead spans read ~0.7–0.92. Only a jaw approaching the full face
  // width counts as "strong" (square/round); an oval's jaw sits well below it.
  wideJaw: 0.80,     // jaw/face-width above this → "strong" jaw (square/round)
  narrowJaw: 0.76,   // jaw/face-width below this → tapered (heart/diamond)
  heartFhMinusJaw: 0.03, // forehead − jaw above this (with narrow jaw) → heart
  diamondForehead: 0.77, // forehead/face-width below this (both narrow) → diamond
  roundMaxR1: 1.45,  // R1 below this with strong jaw → round (else square)
};

/**
 * Classify the face shape from one frame of landmarks. Returns null if required
 * landmarks are missing.
 *
 * Width baseline is the TRUE widest horizontal span across all landmarks (the
 * bizygomatic/cheekbone width), which is far more stable than any single inner
 * cheek pair. Length is oval-top→chin plus a small hairline allowance.
 *
 * IMPORTANT: landmark x and y are each normalized to the video's own width and
 * height independently. On a 16:9 frame (1280×720) the same physical pixel distance
 * gives a LARGER normalized y (÷720) than x (÷1280), so vertical spans are inflated.
 * To compare like-for-like (in pixels) we convert to a common metric by scaling the
 * vertical span by H/W = 1/aspect. With that correction R1 lands near ~1.5 (oval)
 * for a typical adult, matching the published 1.4–1.6 oval band.
 */
export function detectFaceShape(landmarks: Pt[], aspect = 1): FaceShapeResult | null {
  if (!landmarks || landmarks.length < 468) return null;
  const ovalTop = landmarks[OVAL_TOP];
  const chin = landmarks[CHIN];
  if (!ovalTop || !chin) return null;

  // True face width: widest horizontal landmark span (already in x-metric).
  let minX = Infinity, maxX = -Infinity;
  for (const lm of landmarks) {
    if (lm.x < minX) minX = lm.x;
    if (lm.x > maxX) maxX = lm.x;
  }
  const faceW = maxX - minX;
  if (faceW < 1e-4) return null;

  const jawW = avgWidth(landmarks, JAW_PAIRS);
  const foreheadW = avgWidth(landmarks, FOREHEAD_PAIRS);

  // Vertical span ÷ aspect (× H/W) → same pixel metric as the horizontal width.
  const faceLen = (yh(ovalTop, chin) / aspect) * (1 + THRESHOLDS.foreheadAllowance);

  const r1 = faceLen / faceW;
  const jaw = jawW / faceW;
  const forehead = foreheadW / faceW;

  return { shape: classify(r1, jaw, forehead), r1, jaw, forehead };
}

/**
 * Decision tree using R1 (length/width) + jaw and forehead ratios (vs cheekbone).
 * Ordered most-distinctive first; falls through to Oval (the most common shape).
 */
function classify(r1: number, jaw: number, forehead: number): FaceShape {
  const T = THRESHOLDS;

  // Heart: forehead clearly wider than the jaw, with a tapered (narrow) jaw.
  if (forehead - jaw > T.heartFhMinusJaw && jaw < T.narrowJaw) return 'Heart';

  // Diamond: cheekbones the widest — BOTH forehead and jaw narrow & similar.
  if (forehead < T.diamondForehead && jaw < T.narrowJaw
      && Math.abs(forehead - jaw) < 0.06) return 'Diamond';

  // Oblong: noticeably long face.
  if (r1 > T.oblongR1) return 'Oblong';

  // Strong, near-equal jaw & cheek width → Round (short) or Square (longer).
  if (jaw > T.wideJaw) return r1 < T.roundMaxR1 ? 'Round' : 'Square';

  // Oval band (R1 ~1.4–1.6, moderate taper) and everything else.
  return 'Oval';
}

/** Per-shape friendly guidance line shown in the UI. */
export const FACE_SHAPE_GUIDANCE: Record<FaceShape, string> = {
  Oval: 'Lucky you — oval faces suit almost every frame style.',
  Round: 'Angular frames add definition and make a round face look longer.',
  Square: 'Round or oval frames soften a strong, square jawline.',
  Heart: 'Bottom-heavy or rimless frames balance a wider forehead.',
  Oblong: 'Tall, bold frames add width and shorten a longer face.',
  Diamond: 'Cat-eye and oval frames highlight cheekbones beautifully.',
};

// Frame-shape ids from FRAME_SHAPES (types/index.ts):
// 1 Rectangle, 2 Round, 3 Square, 4 Wayfarer, 5 Cat Eye, 6 Aviator,
// 7 Geometric, 8 Hexagonal, 9 Oval, 10 Clubmaster, 11 Sports, 12 Octagon.
const RECOMMENDED: Record<FaceShape, number[]> = {
  Oval: [4, 1, 5, 6, 2],     // Wayfarer, Rectangle, Cat Eye, Aviator, Round
  Round: [1, 3, 4, 5],       // Rectangle, Square, Wayfarer, Cat Eye
  Square: [2, 9, 6, 5],      // Round, Oval, Aviator, Cat Eye
  Heart: [2, 9, 6, 4],       // Round, Oval, Aviator, Wayfarer
  Oblong: [4, 3, 1, 6],      // Wayfarer, Square, Rectangle, Aviator
  Diamond: [5, 9, 2, 4],     // Cat Eye, Oval, Round, Wayfarer
};

/** Frame-shape ids recommended for a given face shape, best first. */
export function shapeToRecommendedFrames(shape: FaceShape): number[] {
  return RECOMMENDED[shape] ?? [];
}

/** Human-readable recommended frame-shape names for the UI (best first, max `n`). */
export function recommendedFrameNames(shape: FaceShape, n = 3): string[] {
  return shapeToRecommendedFrames(shape)
    .slice(0, n)
    .map((id) => FRAME_SHAPES[id])
    .filter(Boolean);
}

/**
 * Rolling-mode smoother: feed per-frame results, read the dominant shape over a
 * short window so the displayed label is stable. Keeps the last `size` samples.
 */
export class FaceShapeSmoother {
  private buf: FaceShape[] = [];
  constructor(private size = 14) {}

  push(shape: FaceShape): FaceShape {
    this.buf.push(shape);
    if (this.buf.length > this.size) this.buf.shift();
    return this.mode();
  }

  mode(): FaceShape {
    const counts = new Map<FaceShape, number>();
    for (const s of this.buf) counts.set(s, (counts.get(s) ?? 0) + 1);
    let best: FaceShape = this.buf[this.buf.length - 1] ?? 'Oval';
    let bestN = 0;
    for (const [s, n] of counts) if (n >= bestN) { best = s; bestN = n; }
    return best;
  }

  get ready(): boolean {
    return this.buf.length >= Math.min(this.size, 8);
  }

  reset(): void {
    this.buf = [];
  }
}
